import React, { useMemo, useState } from 'react'
import { MultiSelect, Select, TextInput } from '@mantine/core'
import { useDidUpdate } from '@mantine/hooks'
import { MagnifyingGlassIcon } from '@radix-ui/react-icons'
import uniq from 'lodash/uniq'

import { Badge } from '@/apiClient'
import { BooleanSelect } from '@/components/common/BooleanSelect'
import { StateSelect } from '@/components/StateSelect'
import { ReporterBase } from '@/types'
import { BADGE_LABELS, LEVELS } from '@/utils/parrot'

export type ReporterFilters = {
  search: string | null
  state: string | null
  level: string | null
  is_active: boolean | null
  has_documents: boolean | null
  certifications: Badge[] | null
}

export const ReporterFilterControls: React.FC<{
  reporters: ReporterBase[]
  onChange: (filters: ReporterFilters) => void
  initialValue: ReporterFilters
}> = ({ reporters, onChange, initialValue }) => {
  const [filters, setFilters] = useState<ReporterFilters>(initialValue)

  const states = useMemo<string[]>(
    () =>
      uniq(reporters.map(r => r.state)).filter(
        (state: string | null | undefined): state is string => Boolean(state),
      ),
    [reporters],
  )

  const handleFilterChange = (newFilters: Partial<ReporterFilters>) => {
    setFilters({ ...filters, ...newFilters })
  }

  useDidUpdate(() => {
    onChange(filters)
  }, [filters])

  return (
    <>
      <TextInput
        rightSection={<MagnifyingGlassIcon color="gray" />}
        placeholder="Search"
        value={filters.search ?? ''}
        onChange={e => handleFilterChange({ search: e.target.value })}
      />
      <Select
        data={LEVELS}
        value={filters.level}
        onChange={level => handleFilterChange({ level })}
        placeholder="All levels"
        clearable
      />
      <StateSelect
        value={filters.state}
        states={states}
        onChange={state => handleFilterChange({ state })}
        placeholder="All states"
        clearable
        searchable
      />
      <BooleanSelect
        trueLabel="Active"
        falseLabel="Inactive"
        value={filters.is_active}
        onChange={is_active =>
          handleFilterChange({
            is_active,
          })
        }
        placeholder="Any status"
        clearable
      />
      <BooleanSelect
        value={filters.has_documents}
        onChange={has_documents =>
          handleFilterChange({
            has_documents,
          })
        }
        placeholder="Documents"
        clearable
      />
      <MultiSelect
        data={Object.entries(BADGE_LABELS).map(([value, label]) => ({
          value,
          label,
        }))}
        value={filters.certifications ?? []}
        onChange={(certifications: Badge[]) =>
          handleFilterChange({ certifications })
        }
        placeholder="Certifications"
        clearable
        searchable
      />
    </>
  )
}
